// src/data/categories.ts

import { ref } from 'vue'
import apiClient from '@/services/api'

export interface CategoryItem {
  id: string
  slug: string
  name: {
    ky: string
    ru: string
    tr: string
  }
  // One or two words for tight places — the phone's category grid, where the
  // full name ("Emlak & Ticari Dükkanlar (Dordoy)") is clipped to nothing.
  shortName: {
    ky: string
    ru: string
    tr: string
  }
  description: {
    ky: string
    ru: string
    tr: string
  }
  icon: string
  iconName: string
  coverImage: string
  count: string
  popularTags: {
    ky: string[]
    ru: string[]
    tr: string[]
  }
  subCategories: Array<{
    id: string
    slug: string
    name: {
      ky: string
      ru: string
      tr: string
    }
  }>
}

/**
 * `count` used to be a hardcoded vanity string ('4.8k', '5.2k', ...). MegaMenu
 * renders it as "<count> активных лотов" on the same screen as the hero counter
 * ("АКТИВНЫХ ЛОТОВ 34") and the category tiles ("6 лотов"), so the landing page
 * showed three mutually contradictory volumes at once — which on an escrow
 * marketplace reads as inflated inventory. The number now comes from live data.
 *
 * Source is GET /api/categories, the platform's own taxonomy endpoint: it counts
 * ACTIVE lots server-side (its per-category totals sum to exactly the hero's
 * active-lot stat), so one small request covers every category and the figure
 * stays correct however large the catalogue grows — unlike a client-side tally,
 * which the backend's perPage cap of 100 would silently truncate. It is also
 * independent of the auction store, whose contents are scoped to whatever the
 * current page happened to filter for.
 *
 * Fetched once per session, lazily — the first time a `count` is actually read.
 */
const lotCounts = ref<Record<string, number> | null>(null)
let lotCountsRequest: Promise<void> | null = null

function ensureLotCounts(): void {
  if (lotCountsRequest) return
  lotCountsRequest = apiClient
    .get<any>('/api/categories')
    .then(res => {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      const tally: Record<string, number> = {}
      for (const row of rows) {
        const key = row?.slug || row?.id
        const total = Number(row?.count)
        if (typeof key === 'string' && key && Number.isFinite(total)) tally[key] = total
      }
      lotCounts.value = tally
      warnOnTaxonomyDrift(rows)
    })
    .catch(() => {
      // Leave the tally unknown rather than inventing a number: the label falls
      // back to an em dash instead of claiming a volume we could not verify.
    })
}

/**
 * This catalogue mirrors the server taxonomy (server/src/database/taxonomy.ts):
 * same category ids and the same sub-category ids, because the sell form writes
 * them onto lots and every filter link (/auctions?category=…&subCategory=…)
 * matches on them. The server list is the source of truth; if the two ever
 * disagree, say so loudly in development instead of quietly filtering to zero.
 */
function warnOnTaxonomyDrift(rows: any[]): void {
  if (!import.meta.env.DEV || rows.length === 0) return
  const server = new Map<string, Set<string>>()
  for (const row of rows) {
    const subs = Array.isArray(row?.subCategories) ? row.subCategories : []
    server.set(String(row?.slug || row?.id), new Set(subs.map((s: any) => String(typeof s === 'string' ? s : s?.id))))
  }
  const problems: string[] = []
  for (const cat of categoryDefinitions) {
    const subs = server.get(cat.slug)
    if (!subs) { problems.push(`category "${cat.slug}" is not in GET /api/categories`); continue }
    for (const sub of cat.subCategories) {
      if (!subs.has(sub.id)) problems.push(`sub-category "${cat.slug}/${sub.id}" is not in GET /api/categories`)
    }
  }
  for (const slug of server.keys()) {
    if (!categoryDefinitions.some(c => c.slug === slug)) problems.push(`server category "${slug}" has no entry in frontend/src/data/categories.ts`)
  }
  if (problems.length) console.warn('[taxonomy drift]\n  ' + problems.join('\n  '))
}

/**
 * Real number of open lots in a category. Returns an em dash — never a blank and
 * never a guess — while the tally is in flight or if the request failed. Once the
 * taxonomy has loaded, a category it does not list genuinely has no open lots, so
 * that reads as a truthful '0'.
 */
function lotCountLabel(slug: string): string {
  ensureLotCounts()
  const tally = lotCounts.value
  return tally ? String(tally[slug] ?? 0) : '—'
}

type CategoryDefinition = Omit<CategoryItem, 'count'>

const categoryDefinitions: CategoryDefinition[] = [
  {
    id: 'electronics',
    slug: 'electronics',
    name: {
      ky: 'Электроника жана Гаджеттер',
      ru: 'Электроника и Гаджеты',
      tr: 'Elektronik ve Cihazlar'
    },
    shortName: {
      ky: 'Электроника',
      ru: 'Электроника',
      tr: 'Elektronik'
    },
    description: {
      ky: 'Смартфондор, ноутбуктар, тиричилик техникасы жана аудио аппаратура',
      ru: 'Смартфоны, ноутбуки, бытовая техника и ТВ',
      tr: 'Akıllı telefonlar, dizüstü bilgisayarlar ve elektronik eşyalar'
    },
    icon: 'Smartphone',
    iconName: 'Smartphone',
    coverImage: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['iPhone 17', 'MacBook Pro', 'IMEI катталган', 'Samsung S24', 'Sony PlayStation'],
      ru: ['iPhone 17', 'MacBook Pro', 'IMEI зарегистрирован', 'Samsung S24', 'Sony PlayStation'],
      tr: ['iPhone 17', 'MacBook Pro', 'IMEI Kayıtlı', 'Samsung S24', 'Sony PlayStation']
    },
    subCategories: [
      { id: 'phones', slug: 'phones', name: { ky: 'Смартфондор жана Планшеттер', ru: 'Смартфоны и Планшеты', tr: 'Telefon ve Tablet' } },
      { id: 'laptops', slug: 'laptops', name: { ky: 'Ноутбуктар жана Компьютерлер', ru: 'Ноутбуки и ПК', tr: 'Bilgisayar ve Donanım' } },
      { id: 'appliances', slug: 'appliances', name: { ky: 'Тиричилик техникасы', ru: 'Бытовая техника', tr: 'Ev Aletleri ve TV' } },
    ]
  },
  {
    id: 'vehicles',
    slug: 'vehicles',
    name: {
      ky: 'Автоунаалар жана Транспорт',
      ru: 'Автомобили и Транспорт',
      tr: 'Otomotiv ve Araçlar'
    },
    shortName: {
      ky: 'Транспорт',
      ru: 'Транспорт',
      tr: 'Araçlar'
    },
    description: {
      ky: 'Жеңил автолор, жолтандабастар, коммерциялык унаалар жана мототехника',
      ru: 'Легковые авто, внедорожники, коммерческий транспорт и мотоциклы',
      tr: 'Binek araçlar, SUV, ticari araçlar ve motosikletler'
    },
    icon: 'Car',
    iconName: 'Car',
    coverImage: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['Toyota Camry', 'Lexus GX', 'Оң руль', 'Бажы төлөнгөн', 'Электромобиль'],
      ru: ['Toyota Camry', 'Lexus GX', 'Правый руль', 'Растаможен', 'Электромобиль'],
      tr: ['Toyota Camry', 'Lexus GX', 'Sağ Direksiyon', 'Gümrük Ödendi', 'Elektrikli Araç']
    },
    subCategories: [
      { id: 'sedans', slug: 'sedans', name: { ky: 'Седандар жана Хэтчбектер', ru: 'Седаны и Хэтчбеки', tr: 'Sedan ve Hatchback' } },
      { id: 'suvs', slug: 'suvs', name: { ky: 'Жолтандабастар жана Кроссоверлер', ru: 'Внедорожники и Кроссоверы', tr: 'SUV ve Crossover' } },
      { id: 'commercial_transport', slug: 'commercial_transport', name: { ky: 'Микроавтобустар жана Жүк ташуучулар', ru: 'Бусы и Грузовики', tr: 'Ticari ve Kamyonet' } },
      { id: 'moto', slug: 'moto', name: { ky: 'Мотоциклдер жана Скутерлер', ru: 'Мотоциклы и Скутеры', tr: 'Motosiklet ve ATV' } },
    ]
  },
  {
    id: 'real-estate',
    slug: 'real-estate',
    name: {
      ky: 'Кыймылсыз мүлк & Соода түйүндөрү (Дордой)',
      ru: 'Недвижимость и Торговые точки (Дордой)',
      tr: 'Emlak & Ticari Dükkanlar (Dordoy)'
    },
    shortName: {
      ky: 'Мүлк',
      ru: 'Недвижимость',
      tr: 'Emlak'
    },
    description: {
      ky: 'Дордой/Кара-Суу контейнерлери, батирлер, жер тилкелери жана коттедждер',
      ru: 'Контейнеры на Дордое, квартиры, участки и коттеджи на Иссык-Куле',
      tr: 'Dordoy konteynerleri, daireler, arsalar ve İssık-Göl yazlıkları'
    },
    icon: 'Building2',
    iconName: 'Building2',
    coverImage: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['Дордой', 'Кызыл китеп', 'Жаңы конуш', 'Ысык-Көл коттедж', 'Элитка'],
      ru: ['Дордой', 'Красная книга', 'Новостройка', 'Иссык-Куль коттедж', 'Элитка'],
      tr: ['Dordoy Pazarı', 'Kırmızı Tapu', 'Yeni Konut', 'İssık-Göl Yazlık', 'Lüks Rezidans']
    },
    subCategories: [
      { id: 'commercial', slug: 'commercial', name: { ky: 'Дордой/Орто-Сай соода түйүндөрү', ru: 'Торговые точки и Контейнеры', tr: 'Pazar Konteyner ve Dükkan' } },
      { id: 'apartments', slug: 'apartments', name: { ky: 'Батирлер (Элитка жана Эски)', ru: 'Квартиры (Новостройки и Вторичка)', tr: 'Daireler ve Rezidanslar' } },
      { id: 'houses', slug: 'houses', name: { ky: 'Жеке үйлөр жана Коттедждер', ru: 'Дома и Коттеджи', tr: 'Müstakil Ev ve Villalar' } },
      { id: 'lands', slug: 'lands', name: { ky: 'Жер тилкелери жана Фермалар', ru: 'Земельные участки и Фермы', tr: 'Arsa ve Tarım Arazileri' } },
    ]
  },
  {
    id: 'livestock',
    slug: 'livestock',
    name: {
      ky: 'Мал чарбасы & Айыл чарба (Мал базары)',
      ru: 'Скотный рынок и Сельское хозяйство',
      tr: 'Hayvancılık & Köy Mal Pazarı'
    },
    shortName: {
      ky: 'Мал базары',
      ru: 'Скот и агро',
      tr: 'Hayvancılık'
    },
    description: {
      ky: 'Саан уйлар, асыл тукум букалар, күлүк жылкылар, Арашан/Гиссар кочкорлору жана тоют чөптөр',
      ru: 'Дойные коровы, племенные быки, скаковые лошади, породистые бараны и корма',
      tr: 'Sağmal inekler, damızlık boğalar, atlar, koçlar, yem ve köy ürünleri'
    },
    icon: 'Wheat',
    iconName: 'Wheat',
    coverImage: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['Саан уй', 'Күлүк жылкы', 'Арашан кочкор', 'Бука', 'Тоют чөп'],
      ru: ['Дойная корова', 'Скаковая лошадь', 'Арашан баран', 'Племенной бык', 'Сено и корма'],
      tr: ['Sağmal İnek', 'Yarış Atı', 'Araşan Koç', 'Damızlık Boğa', 'Yem ve Saman']
    },
    subCategories: [
      { id: 'cows', slug: 'cows', name: { ky: 'Саан уйлар жана Букалар', ru: 'Коровы и Быки', tr: 'İnek ve Damızlık Boğalar' } },
      { id: 'horses', slug: 'horses', name: { ky: 'Күлүк жылкылар жана Тайлар', ru: 'Лошади и Жеребята', tr: 'Atlar ve Yarış Tayları' } },
      { id: 'sheep', slug: 'sheep', name: { ky: 'Койлор жана Кочкорлор (Арашан)', ru: 'Бараны и Овцы (Арашан)', tr: 'Koyun ve Koçlar (Araşan)' } },
      { id: 'poultry', slug: 'poultry', name: { ky: 'Үй канаттуулары (Тоок, Каз)', ru: 'Домашняя птица', tr: 'Kümes Hayvanları' } },
      { id: 'feed', slug: 'feed', name: { ky: 'Тоют, Чөп жана Жем', ru: 'Корма, Сено и Зерно', tr: 'Yem, Saman ve Tahıl' } },
    ]
  },
  {
    id: 'jewelry',
    slug: 'jewelry',
    name: {
      ky: 'Зергерлик буюмдар & Сааттар',
      ru: 'Ювелирные изделия и Часы',
      tr: 'Mücevher ve Saatler'
    },
    shortName: {
      ky: 'Зергерлик',
      ru: 'Украшения',
      tr: 'Mücevher'
    },
    description: {
      ky: 'Алтын, күмүш, улуттук кыргыз зер буюмдары жана швейцариялык сааттар',
      ru: 'Золото, серебро, национальные украшения и швейцарские часы',
      tr: 'Altın, gümüş, geleneksel takılar ve lüks saatler'
    },
    icon: 'Gem',
    iconName: 'Gem',
    coverImage: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['585 Алтын', 'Улуттук билерик', 'Rolex', 'Бриллиант шакек'],
      ru: ['585 Золото', 'Национальный браслет', 'Rolex', 'Бриллиант'],
      tr: ['585 Altın', 'Geleneksel Bilezik', 'Rolex', 'Pırlanta Yüzük']
    },
    subCategories: [
      { id: 'gold', slug: 'gold', name: { ky: 'Алтын жана Бриллианттар', ru: 'Золото и Бриллианты', tr: 'Altın ve Pırlanta' } },
      { id: 'watches', slug: 'watches', name: { ky: 'Эркектер жана Аялдар сааттары', ru: 'Наручные часы', tr: 'Lüks Kol Saatleri' } },
      { id: 'national', slug: 'national', name: { ky: 'Улуттук кыргыз зер буюмдары', ru: 'Национальные украшения', tr: 'Geleneksel Takılar' } },
    ]
  },
  {
    // Slug must match the backend taxonomy ("antiques", never "art"), otherwise
    // /auctions?category=... filters to zero lots.
    id: 'antiques',
    slug: 'antiques',
    name: {
      ky: 'Өнөр, Антиквариат & Салттуу буюмдар',
      ru: 'Искусство, Антиквариат и Традиции',
      tr: 'Sanat, Antika & Koleksiyon'
    },
    shortName: {
      ky: 'Антиквариат',
      ru: 'Антиквариат',
      tr: 'Antika'
    },
    description: {
      ky: 'Кыргыз шырдактары, колуктунун себи, тарыхый тыйындар жана сүрөттөр',
      ru: 'Кыргызские шырдаки, национальный быт, монеты и картины',
      tr: 'Geleneksel Kırgız keçe halıları (şırdak), antika paralar ve sanat eserleri'
    },
    icon: 'Palette',
    iconName: 'Palette',
    // Rolled tribal felt/wool rugs — the closest stock stand-in for a shyrdak.
    // (The previous photo was a modern glass-and-timber house.)
    coverImage: 'https://images.unsplash.com/photo-1616395442106-1b927fee41cd?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['Шырдак', 'Боз үй жабдыктары', 'Антиквариат', 'Кол өнөрчүлүк'],
      ru: ['Шырдак', 'Юрта', 'Антиквариат', 'Ручная работа'],
      tr: ['Şırdak Halısı', 'Kırgız Çadırı', 'Antika Para', 'El Sanatları']
    },
    subCategories: [
      { id: 'shyrdak', slug: 'shyrdak', name: { ky: 'Шырдактар жана Кийиз буюмдары', ru: 'Шырдаки и Войлок', tr: 'Şırdak ve Keçe Halılar' } },
      { id: 'coins', slug: 'coins', name: { ky: 'Нумизматика жана Тыйындар', ru: 'Монеты и Банкноты', tr: 'Madeni Paralar ve Pullar' } },
      { id: 'paintings', slug: 'paintings', name: { ky: 'Картиналар жана Сүрөттөр', ru: 'Картины', tr: 'Tablolar ve Çizimler' } },
    ]
  },
  {
    // "machinery" is a real top-level category in the server taxonomy since
    // 2026-09 (it used to be storefront-only, so this tile always counted 0).
    id: 'machinery',
    slug: 'machinery',
    name: {
      ky: 'Атайын жана Айыл чарба техникасы',
      ru: 'Спецтехника и Сельхозтехника',
      tr: 'İş ve Tarım Makineleri'
    },
    shortName: {
      ky: 'Техника',
      ru: 'Спецтехника',
      tr: 'Makineler'
    },
    description: {
      ky: 'Тракторлор, комбайндар, экскаваторлор жана айыл чарба агрегаттары',
      ru: 'Тракторы, комбайны, экскаваторы и навесное оборудование',
      tr: 'Traktörler, biçerdöverler, kazıcılar ve tarım ekipmanları'
    },
    icon: 'Tractor',
    iconName: 'Tractor',
    // Tractor working a harvested field. (The previous photo was a men's suit jacket.)
    coverImage: 'https://images.unsplash.com/photo-1594771804886-a933bb2d609b?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['МТЗ-82', 'Экскаватор', 'Соко', 'Комбайн Клаас'],
      ru: ['МТЗ-82', 'Экскаватор', 'Плуг', 'Комбайн Claas'],
      tr: ['MTZ-82 Traktör', 'Ekskavatör', 'Saban ve Pulluk', 'Claas Biçerdöver']
    },
    subCategories: [
      { id: 'tractors', slug: 'tractors', name: { ky: 'Тракторлор (Беларус, ЮМЗ)', ru: 'Тракторы (МТЗ, ЮМЗ)', tr: 'Traktörler (Belarus, MTZ)' } },
      { id: 'construction', slug: 'construction', name: { ky: 'Курулуш техникасы', ru: 'Строительная техника', tr: 'İnşaat ve İş Makineleri' } },
      { id: 'attachments', slug: 'attachments', name: { ky: 'Агрегаттар (Соко, Пресс)', ru: 'Навесное оборудование', tr: 'Tarım Aletleri ve Ekipman' } },
    ]
  },
  {
    id: 'home-goods',
    slug: 'home-goods',
    name: {
      ky: 'Эмерек жана Үй буюмдары',
      ru: 'Мебель и Товары для дома',
      tr: 'Mobilya ve Ev Eşyaları'
    },
    shortName: {
      ky: 'Эмерек',
      ru: 'Мебель',
      tr: 'Mobilya'
    },
    description: {
      ky: 'Диван-керебеттер, уктоочу жана ашкана эмеректери, үй декору',
      ru: 'Диваны, спальни, кухонная мебель и предметы декора',
      tr: 'Koltuklar, yatak odası, mutfak mobilyaları ve ev dekorasyonu'
    },
    icon: 'Sofa',
    iconName: 'Sofa',
    coverImage: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1200&q=85',
    popularTags: {
      ky: ['Диван', 'Керебет', 'Тамактануу столу', 'Шкаф', 'Жумшак эмерек'],
      ru: ['Диван', 'Кровать', 'Обеденный стол', 'Шкаф-купе', 'Мягкая мебель'],
      tr: ['Koltuk Takımı', 'Yatak', 'Yemek Masası', 'Gardırop', 'Oturma Grubu']
    },
    subCategories: [
      { id: 'living-room', slug: 'living-room', name: { ky: 'Зал жана жумшак эмерек', ru: 'Диваны и мягкая мебель', tr: 'Koltuk ve Oturma Grubu' } },
      { id: 'bedroom', slug: 'bedroom', name: { ky: 'Уктоочу бөлмө эмеректери', ru: 'Кровати, шкафы и матрасы', tr: 'Yatak Odası Mobilyaları' } },
      { id: 'kitchen-dining', slug: 'kitchen-dining', name: { ky: 'Ашкана жана тамактануу эмеректери', ru: 'Кухонная и обеденная мебель', tr: 'Mutfak ve Yemek Odası' } },
      { id: 'home-decor', slug: 'home-decor', name: { ky: 'Үй декору жана текстили', ru: 'Декор и текстиль для дома', tr: 'Ev Dekorasyonu ve Tekstil' } },
    ]
  }
]

// `count` is an accessor, not stored data: reading it inside a Vue computed (as
// MegaMenu does) subscribes that computed to the live tally, so the label swaps
// from the em-dash placeholder to the real number as soon as it lands.
export const platformCategories: CategoryItem[] = categoryDefinitions.map(definition => ({
  ...definition,
  get count(): string {
    return lotCountLabel(definition.slug)
  },
}))
